import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "435",
  "slug": "what-is-an-archipelago",
  "title": "What Is an Archipelago?",
  "excerpt": "From Indonesia's seventeen thousand islands to the chain that makes up Hawaii, archipelagos are some of the most striking features on any map. Here is what they are and how they form.",
  "date": "2026-06-14",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1500531279542-fc8490c8ea4d?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGlzbGFuZHMlMjBhZXJpYWx8ZW58MXwwfHx8MTc4MTQyNTA3OHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "An archipelago is a group or chain of islands clustered together in a sea, ocean, or lake. They form in several ways, including volcanic hotspots, drowned coastlines, and coral growth, and they include some of the most recognizable shapes on Earth.",
  "content": [
    {
      "type": "paragraph",
      "text": "Look at a map of Southeast Asia, the Caribbean, or the Aegean Sea and you will see them immediately: scatterings and chains of islands, some tiny, some enormous, grouped together like a constellation dropped into the water. That is an archipelago, and the word covers everything from a handful of rocky outcrops to a nation built across thousands of separate landmasses."
    },
    {
      "type": "heading",
      "text": "The basic definition"
    },
    {
      "type": "paragraph",
      "text": "An archipelago is a group of islands that lie close together and are usually considered as a set rather than individually. The islands can sit in an ocean, a sea, or even a large lake. The term comes from old Italian and Greek roots originally referring to the Aegean Sea, which is dotted with hundreds of Greek islands, before it broadened to mean any island group."
    },
    {
      "type": "heading",
      "text": "The biggest archipelagos in the world"
    },
    {
      "type": "list",
      "items": [
        "Indonesia is the largest by number, made up of more than 17,000 islands stretching across thousands of kilometers.",
        "The Philippines is built from over 7,000 islands.",
        "The Canadian Arctic Archipelago is the largest by land area, a sprawl of huge, mostly uninhabited islands north of the mainland.",
        "The Malay Archipelago, which includes Indonesia, the Philippines, and neighbors, is the largest archipelago region on the planet.",
        "Japan, Greece, the Maldives, and the Hawaiian Islands are other famous examples, each with a very different look from above."
      ]
    },
    {
      "type": "heading",
      "text": "How archipelagos form"
    },
    {
      "type": "paragraph",
      "text": "Islands do not cluster together by accident. Most archipelagos owe their existence to one of a few geological processes, and you can often guess which one by the shape and arrangement of the islands."
    },
    {
      "type": "list",
      "items": [
        "Volcanic hotspots: Hawaii is the classic case. A stationary plume of magma deep in the mantle punches through a moving tectonic plate, building one volcanic island after another in a line, like a conveyor belt of land.",
        "Volcanic arcs: where one tectonic plate dives beneath another, a curved chain of volcanoes can rise, forming arcs like much of Indonesia and the Aleutian Islands.",
        "Drowned coastlines: when sea levels rise, the tops of former hills and mountains are left poking above the water as islands, creating intricate continental archipelagos.",
        "Coral growth: in warm tropical seas, coral reefs build up over time into low islands and atolls, as seen across the Maldives and much of the Pacific."
      ]
    },
    {
      "type": "heading",
      "text": "When a whole country is an archipelago"
    },
    {
      "type": "paragraph",
      "text": "For some nations, being an archipelago shapes almost everything about daily life and politics. Indonesia and the Philippines must knit together hundreds of inhabited islands with ferries, flights, and undersea cables, and a single national identity has to span dozens of languages and cultures separated by water. Archipelagic countries also control vast areas of ocean: under international law, the waters around their islands extend their territory far out to sea, giving small island nations rights over fishing grounds and seabed resources many times larger than their land area."
    },
    {
      "type": "paragraph",
      "text": "That close relationship with the sea cuts both ways. Low-lying coral archipelagos like the Maldives and many Pacific nations are among the most exposed places on Earth to rising sea levels, since much of their land sits only a meter or two above the waves. For them, the geography that defines their beauty is also their greatest vulnerability."
    },
    {
      "type": "heading",
      "text": "Why they are fun to spot from space"
    },
    {
      "type": "paragraph",
      "text": "Archipelagos are some of the most distinctive features in satellite imagery. A volcanic chain forms a neat line of green cones; a coral archipelago shows up as rings and crescents of pale turquoise reef; a drowned coastline looks like a jigsaw of fragments separated by narrow channels. Once you learn the patterns, you can often narrow down a region from the island shapes alone."
    },
    {
      "type": "paragraph",
      "text": "A quick note on a common mix-up: an archipelago is a group of islands, while an atoll is a single ring-shaped reef and island enclosing a lagoon. Many archipelagos, especially in the Pacific, are made up largely of atolls, but the two words describe different scales. The archipelago is the whole chain; the atoll is one of the rings within it."
    }
  ]
};
